import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Plus, Flag, Pencil, Trash2, ArrowRight, CalendarClock } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SharedProps, EventData, PropertyEventData } from '@/types/shared'

interface EventsProps extends SharedProps {
  events: EventData[]
  propertyEvents: PropertyEventData[]
}

const IMPACT_COLORS: Record<string, string> = {
  minor: 'bg-slate-100 text-slate-700',
  moderate: 'bg-amber-100 text-amber-700',
  major: 'bg-orange-100 text-orange-700',
  extreme: 'bg-rose-100 text-rose-700',
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  return `${s.toLocaleDateString('en-GB', opts)} - ${e.toLocaleDateString('en-GB', opts)}`
}

export default function Events({
  currentUser,
  property,
  properties,
  events,
  propertyEvents,
}: EventsProps) {
  const base = property.basePath

  const [createOpen, setCreateOpen] = useState(false)
  const [editEvent, setEditEvent] = useState<EventData | null>(null)
  const [assignEvent, setAssignEvent] = useState<EventData | null>(null)
  // The assign dialog steps aside while its review is open, and comes back, choice intact, on "Back".
  const [assignHidden, setAssignHidden] = useState(false)
  const review = usePricingReview()

  // Create form state
  const [form, setForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    impact_level: 'moderate',
    demand_multiplier: '',
    notes: '',
  })

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    impact_level: '',
    demand_multiplier: '',
    notes: '',
  })

  const [overrideMultiplier, setOverrideMultiplier] = useState('')
  const [saving, setSaving] = useState(false)

  const assignedEventIds = new Set(propertyEvents.map((pe) => pe.eventId))

  function resetCreateForm() {
    setForm({
      name: '',
      start_date: '',
      end_date: '',
      impact_level: 'moderate',
      demand_multiplier: '',
      notes: '',
    })
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    const data: Record<string, string> = { ...form }
    if (!data.demand_multiplier) delete data.demand_multiplier
    if (!data.notes) delete data.notes
    router.post(`${base}/events`, data, {
      onFinish: () => {
        setSaving(false)
        setCreateOpen(false)
        resetCreateForm()
      },
    })
  }

  function handleEdit(e: React.FormEvent) {
    e.preventDefault()
    if (!editEvent) return
    setSaving(true)
    const data: Record<string, string> = { ...editForm }
    if (!data.demand_multiplier) delete data.demand_multiplier
    if (!data.notes) delete data.notes
    router.patch(`${base}/events/${editEvent.id}`, data, {
      onFinish: () => {
        setSaving(false)
        setEditEvent(null)
      },
    })
  }

  function handleDelete(event: EventData) {
    if (!confirm(`Delete event "${event.name}"? This will remove it from all properties.`)) return
    router.delete(`${base}/events/${event.id}`)
  }

  // Assigning an event is a pricing save on this hotel, so it goes through the same server review as
  // the Events tab (change safety §5.17.1). The review endpoint is the tab's; the save is unchanged.
  function handleAssign(e: React.FormEvent) {
    e.preventDefault()
    if (!assignEvent) return
    const eventId = assignEvent.id
    const data: Record<string, string> = {}
    if (overrideMultiplier) data.override_multiplier = overrideMultiplier
    setAssignHidden(true)
    review.open(
      `${base}/pricing-rules/events/review`,
      { operation: 'assign', event_id: eventId, ...data },
      () =>
        inertiaSave('post', `${base}/events/${eventId}/assign`, data, {
          onStart: () => setSaving(true),
          onFinish: () => {
            setSaving(false)
            setAssignEvent(null)
            setAssignHidden(false)
            setOverrideMultiplier('')
          },
        }),
      { onBack: () => setAssignHidden(false) }
    )
  }

  function openEdit(event: EventData) {
    setEditForm({
      name: event.name,
      start_date: event.startDate,
      end_date: event.endDate,
      impact_level: event.impactLevel,
      demand_multiplier: event.demandMultiplier?.toString() || '',
      notes: event.notes || '',
    })
    setEditEvent(event)
  }

  return (
    <OperatorLayout title="Event Catalogue" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        {/* Header */}
        <div className="animate-slide-in flex items-center justify-between" style={{ animationDelay: '0ms' }}>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">Event Catalogue</h1>
              <HelpTooltip
                content="Create and manage events across all properties. To assign events to a specific property, go to Pricing Rules > Events."
                learnMore="/guides/events"
              />
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Manage events that can affect rate computation. Assign events to properties from Pricing Rules &gt; Events.
            </p>
          </div>
          <Button onClick={() => { resetCreateForm(); setCreateOpen(true) }} className="rounded-lg">
            <Plus className="mr-2 h-4 w-4" />
            New Event
          </Button>
        </div>

        {/* All Events */}
        <div className="animate-slide-in" style={{ animationDelay: '50ms' }}>
          {events.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <CalendarClock className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No events yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Create an event to capture demand spikes your booking history can't predict, then assign it to a property.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Impact</TableHead>
                    <TableHead className="text-right">Multiplier</TableHead>
                    <TableHead className="text-right">Properties</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((event) => {
                    const isAssigned = assignedEventIds.has(event.id)
                    return (
                      <TableRow key={event.id}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{event.name}</span>
                            {event.notes && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">{event.notes}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDateRange(event.startDate, event.endDate)}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={cn('text-[11px] capitalize', IMPACT_COLORS[event.impactLevel])}>
                            {event.impactLevel}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">
                          {event.demandMultiplier?.toFixed(2)}x
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {event.propertyCount}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {!isAssigned && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-xs"
                                onClick={() => setAssignEvent(event)}
                              >
                                <ArrowRight className="mr-1 h-3 w-3" />
                                Assign
                              </Button>
                            )}
                            {isAssigned && (
                              <Badge variant="secondary" className="text-[10px]">Assigned</Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => openEdit(event)}
                            >
                              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-rose-500 hover:text-rose-600"
                              onClick={() => handleDelete(event)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Create Event Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Create Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="flex min-h-0 flex-1 flex-col">
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="event-name">Name</Label>
                <Input
                  id="event-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Edinburgh Fringe Festival"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="event-start">Start Date</Label>
                  <Input
                    id="event-start"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-end">End Date</Label>
                  <Input
                    id="event-end"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="event-impact">Impact Level</Label>
                  <Select value={form.impact_level} onValueChange={(v) => setForm({ ...form, impact_level: v })}>
                    <SelectTrigger id="event-impact">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Minor (1.15x)</SelectItem>
                      <SelectItem value="moderate">Moderate (1.30x)</SelectItem>
                      <SelectItem value="major">Major (1.50x)</SelectItem>
                      <SelectItem value="extreme">Extreme (2.00x)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event-multiplier">Custom Multiplier</Label>
                  <Input
                    id="event-multiplier"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.demand_multiplier}
                    onChange={(e) => setForm({ ...form, demand_multiplier: e.target.value })}
                    placeholder="Leave blank for default"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="event-notes">Notes</Label>
                <Input
                  id="event-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional notes about this event"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Creating...' : 'Create Event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editEvent !== null} onOpenChange={(open) => !open && setEditEvent(null)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="flex min-h-0 flex-1 flex-col">
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-start">Start Date</Label>
                  <Input
                    id="edit-start"
                    type="date"
                    value={editForm.start_date}
                    onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-end">End Date</Label>
                  <Input
                    id="edit-end"
                    type="date"
                    value={editForm.end_date}
                    onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-impact">Impact Level</Label>
                  <Select value={editForm.impact_level} onValueChange={(v) => setEditForm({ ...editForm, impact_level: v })}>
                    <SelectTrigger id="edit-impact">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Minor (1.15x)</SelectItem>
                      <SelectItem value="moderate">Moderate (1.30x)</SelectItem>
                      <SelectItem value="major">Major (1.50x)</SelectItem>
                      <SelectItem value="extreme">Extreme (2.00x)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-multiplier">Custom Multiplier</Label>
                  <Input
                    id="edit-multiplier"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editForm.demand_multiplier}
                    onChange={(e) => setEditForm({ ...editForm, demand_multiplier: e.target.value })}
                    placeholder="Leave blank for default"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Input
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditEvent(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Event Dialog */}
      <Dialog open={assignEvent !== null && !assignHidden} onOpenChange={(open) => { if (!open) { setAssignEvent(null); setOverrideMultiplier('') } }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Assign Event to {property.name}</DialogTitle>
            <DialogDescription>
              The engine prices the event into this hotel&apos;s nights. You see the change before it is saved.
            </DialogDescription>
          </DialogHeader>
          {assignEvent && (
            <form onSubmit={handleAssign} className="flex min-h-0 flex-1 flex-col">
              <DialogBody className="space-y-4">
                <div className="rounded-lg bg-slate-50 p-3 text-sm">
                  <p className="font-medium">{assignEvent.name}</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    {formatDateRange(assignEvent.startDate, assignEvent.endDate)}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Default multiplier: {assignEvent.demandMultiplier?.toFixed(2)}x ({assignEvent.impactLevel})
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assign-multiplier">Override Multiplier (optional)</Label>
                  <Input
                    id="assign-multiplier"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={overrideMultiplier}
                    onChange={(e) => setOverrideMultiplier(e.target.value)}
                    placeholder="Leave blank to use event default"
                  />
                  <p className="text-xs text-muted-foreground">
                    Set a property-specific multiplier, or leave blank to use the event's default.
                  </p>
                </div>
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-lg" onClick={() => { setAssignEvent(null); setOverrideMultiplier('') }}>Cancel</Button>
                <Button type="submit" className="rounded-lg" disabled={saving}>
                  {saving ? 'Assigning...' : 'Assign Event'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      {review.dialog}
    </OperatorLayout>
  )
}
